import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ShieldCheck, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AuthGate({ children }) {
  const [authed, setAuthed] = useState(null); // null=checking, true/false
  const [step, setStep] = useState('email'); // email | otp
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpFallback, setOtpFallback] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check existing session
  useEffect(() => {
    const token = localStorage.getItem('shafafiya_token');
    if (!token) { setAuthed(false); return; }
    fetch(`${API}/api/auth/verify-session`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) return r.json(); throw new Error(); })
      .then(() => setAuthed(true))
      .catch(() => { localStorage.removeItem('shafafiya_token'); setAuthed(false); });
  }, []);

  const requestOtp = async () => {
    setError('');
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      let data = {};
      try { data = await res.json(); } catch (_) { /* non-JSON */ }
      if (!res.ok) {
        setError(data.detail || `Request failed (${res.status})`);
        return;
      }
      if (data.otp_preview) setOtpFallback(data.otp_preview);
      setStep('otp');
    } catch (err) {
      setError(err?.message ? `Network error: ${err.message}` : 'Network error — could not reach the server');
    }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp })
      });
      let data = {};
      try { data = await res.json(); } catch (_) { /* non-JSON */ }
      if (!res.ok) {
        setError(data.detail || `Verification failed (${res.status})`);
        return;
      }
      localStorage.setItem('shafafiya_token', data.token);
      setAuthed(true);
    } catch (err) {
      setError(err?.message ? `Network error: ${err.message}` : 'Network error — could not reach the server');
    }
    finally { setLoading(false); }
  };

  // Checking session
  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 size={32} className="text-teal-600 animate-spin" />
      </div>
    );
  }

  // Authenticated
  if (authed) return children;

  // Gate
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4" style={{
      backgroundImage: 'linear-gradient(rgba(0,0,0,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.02) 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Lock badge */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-teal-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-600/25">
            <Mail size={22} className="text-white" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl text-[#0A0A0A]">Interactive Walkthrough</h1>
          <p className="font-body text-sm text-[#737373] mt-1">Sign in with your @malaffi.ae email</p>
        </div>

        <AnimatePresence mode="wait">
          {step === 'email' ? (
            <motion.div key="email" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
              className="bg-white rounded-2xl border border-black/5 shadow-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-teal-600/10 flex items-center justify-center mb-5">
                <Mail size={22} className="text-teal-600" />
              </div>
              <h2 className="font-heading font-bold text-xl text-[#0A0A0A] mb-1">Enter your email</h2>
              <p className="font-body text-sm text-[#737373] mb-6">We'll send a verification code to your email</p>

              <label className="text-sm font-body font-bold text-[#0A0A0A] mb-2 block">Email address</label>
              <input
                data-testid="gate-email-input"
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && requestOtp()}
                placeholder="you@malaffi.ae"
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] text-sm font-body text-[#0A0A0A] focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 mb-2"
              />
              <p className="text-[11px] font-body text-[#737373] mb-4">Only <span className="font-bold">@malaffi.ae</span> emails are allowed</p>

              {error && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 mb-4">
                  <AlertCircle size={14} className="text-red-500" />
                  <span className="text-[12px] font-body text-red-600">{error}</span>
                </div>
              )}

              <button
                data-testid="gate-send-otp-button"
                onClick={requestOtp}
                disabled={loading || !email.trim()}
                className="w-full py-3 rounded-xl bg-teal-600 text-white font-body font-bold text-sm flex items-center justify-center gap-2 hover:bg-teal-700 disabled:opacity-50 transition-colors"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <><ArrowRight size={16} /> Continue</>}
              </button>
            </motion.div>
          ) : (
            <motion.div key="otp" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl border border-black/5 shadow-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-teal-600/10 flex items-center justify-center mb-5">
                <ShieldCheck size={22} className="text-teal-600" />
              </div>
              <h2 className="font-heading font-bold text-xl text-[#0A0A0A] mb-1">Verify your email</h2>
              <p className="font-body text-sm text-[#737373] mb-2">Enter the 6-digit code sent to</p>
              <p className="font-body text-sm font-bold text-[#0A0A0A] mb-5">{email}</p>

              {otpFallback && (
                <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 mb-4">
                  <p className="text-[11px] font-body text-amber-700">Email delivery pending — your OTP: <span className="font-bold text-amber-900 text-base">{otpFallback}</span></p>
                  <p className="text-[10px] font-body text-amber-600 mt-1">Verify your domain at resend.com/domains to enable email delivery</p>
                </div>
              )}

              <label className="text-sm font-body font-bold text-[#0A0A0A] mb-2 block">OTP Code</label>
              <input
                data-testid="gate-otp-input"
                type="text"
                value={otp}
                onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                onKeyDown={e => e.key === 'Enter' && otp.length === 6 && verifyOtp()}
                placeholder="Enter 6-digit code"
                maxLength={6}
                className="w-full px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] text-lg font-heading font-bold text-center text-[#0A0A0A] tracking-[0.5em] focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 mb-4"
              />

              {error && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 mb-4">
                  <AlertCircle size={14} className="text-red-500" />
                  <span className="text-[12px] font-body text-red-600">{error}</span>
                </div>
              )}

              <button
                data-testid="gate-verify-button"
                onClick={verifyOtp}
                disabled={loading || otp.length !== 6}
                className="w-full py-3 rounded-xl bg-teal-600 text-white font-body font-bold text-sm flex items-center justify-center gap-2 hover:bg-teal-700 disabled:opacity-50 transition-colors mb-3"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <><ShieldCheck size={16} /> Verify & Enter</>}
              </button>

              <button
                data-testid="gate-back-button"
                onClick={() => { setStep('email'); setOtp(''); setError(''); setOtpFallback(''); }}
                className="w-full py-2 rounded-xl text-[#737373] font-body text-sm hover:bg-[#FAFAFA] transition-colors"
              >
                Use a different email
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
